import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Box,
  Typography,
  Grid,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import appointmentsApi from '../../../api/appointments';
import clientsApi from '../../../api/clientsApi';
import { useServiceCategories, useServicesByCategory } from '../../../hooks/useServices';
import { useAppTheme } from '../../../contexts/ThemeContext';
import { getTenantCacheKey } from '../../../utils/auth';
import { useCurrency } from '../../../hooks/useCurrency';
import { getCurrencySymbol, formatCurrency } from '../../../utils/currency';
import { formatClientName } from '../../../utils/displayNames';
import DevOnly from '../../../components/DevOnly';
import SyncStatusIndicator from '../../../components/SyncStatusIndicator';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' }
];

const STATUS_FALLBACK = 'scheduled';

const STATUS_NORMALIZATION_MAP = {
  waiting: STATUS_FALLBACK,
  waitlist: STATUS_FALLBACK,
  hold: STATUS_FALLBACK,
  complete: 'completed',
  completed: 'completed',
  'in-progress': 'in_progress',
  inprogress: 'in_progress',
  'no-show': 'no_show',
  noshow: 'no_show'
};

const normalizeStatus = (status) => {
  if (!status) {
    return STATUS_FALLBACK;
  }

  const prepared = status
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  const normalized = STATUS_NORMALIZATION_MAP[prepared] || prepared.replace(/-/g, '_');

  return STATUS_OPTIONS.some(option => option.value === normalized) ? normalized : STATUS_FALLBACK;
};

const dialogPaperSx = {
  width: { xs: '100%', sm: 720, md: 960 },
  maxWidth: '92vw'
};

const halfWidthItemSx = {
  width: '100%',
  minWidth: { xs: '100%', md: 320 }
};

const AppointmentWriteDialog = ({ 
  open, 
  onClose, 
  appointment = null, 
  selectedDate = null,
  selectedTimeSlot = null 
}) => {
  const queryClient = useQueryClient();
  const { currentTheme = {} } = useAppTheme() || {};
  const { currency } = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);
  
  // Initialize form data
  const getInitialFormData = () => {
    const now = new Date();
    return {
      client_id: '',
      user_id: '',
      location_id: '',
      start_date: now,
      end_date: new Date(now.getTime() + 30 * 60000),
      notes: '',
      status: STATUS_FALLBACK,
      services: []
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData());
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      // Always reset selectedCategory when opening dialog
      setSelectedCategory('');
      setValidationErrors({});

      if (appointment) {
        // Editing existing appointment
        setFormData({
          client_id: appointment.client_id || '',
          user_id: appointment.user_id || '',
          location_id: appointment.location_id || '',
          start_date: new Date(appointment.start_date),
          end_date: new Date(appointment.end_date),
          notes: appointment.notes || '',
          status: normalizeStatus(appointment.status),
          services: appointment.services || []
        });

        // If appointment has services, try to set the category from the first service
        if (appointment.services && appointment.services.length > 0) {
          const firstService = appointment.services[0];
          if (firstService.category_id) {
            setSelectedCategory(firstService.category_id);
          }
        }
      } else if (selectedTimeSlot && selectedTimeSlot.start) {
        // Creating new appointment from calendar click
        const startDate = selectedTimeSlot.start instanceof Date ? selectedTimeSlot.start : new Date(selectedTimeSlot.start);
        const endDate = selectedTimeSlot.end
          ? (selectedTimeSlot.end instanceof Date ? selectedTimeSlot.end : new Date(selectedTimeSlot.end))
          : new Date(startDate.getTime() + 30 * 60000);

        setFormData({
          ...getInitialFormData(),
          start_date: startDate,
          end_date: endDate
        });
      } else if (selectedDate) {
        // Creating new appointment with selected date
        const startDate = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
        setFormData({
          ...getInitialFormData(),
          start_date: startDate,
          end_date: new Date(startDate.getTime() + 30 * 60000)
        });
      } else {
        // Reset to default
        setFormData(getInitialFormData());
      }
    } else {
      // When dialog closes, reset everything
      setFormData(getInitialFormData());
      setSelectedCategory('');
      setValidationErrors({});
      setShowDeleteConfirm(false);
    }
  }, [open, appointment, selectedDate, selectedTimeSlot]);

  // Fetch clients
  const { data: clientsResponse, isLoading: clientsLoading } = useQuery({
    queryKey: [getTenantCacheKey('clients')],
    queryFn: () => clientsApi.getClients(),
    staleTime: 5 * 60 * 1000,
  });
  
  // Extract clients array from response
  const clientsData = clientsResponse?.clients || [];

  const appointmentClientId = appointment?.client_id;

  const { data: appointmentClientDetails } = useQuery({
    queryKey: [getTenantCacheKey('client'), appointmentClientId],
    queryFn: () => clientsApi.getClient(appointmentClientId),
    enabled: !!appointmentClientId && !clientsData.some(client => client.id === appointmentClientId),
    staleTime: 5 * 60 * 1000,
  });

  const allClients = useMemo(() => {
    if (!appointmentClientDetails) return clientsData;
    const exists = clientsData.some(client => client.id === appointmentClientDetails.id);
    return exists ? clientsData : [...clientsData, appointmentClientDetails];
  }, [clientsData, appointmentClientDetails]);

  // Fetch providers
  const { data: providersData = [], isLoading: providersLoading } = useQuery({
    queryKey: [getTenantCacheKey('providers')],
    queryFn: () => appointmentsApi.getProviders(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch locations
  const { data: locationsData = [], isLoading: locationsLoading } = useQuery({
    queryKey: [getTenantCacheKey('locations')],
    queryFn: () => appointmentsApi.getLocations(),
    staleTime: 5 * 60 * 1000,
  });

  // Debug logging for location issues
  useEffect(() => {
    if (appointment && locationsData.length > 0) {
      const appointmentLocationId = appointment.location_id;
      const locationExists = locationsData.some(loc => loc.id === appointmentLocationId);
      if (appointmentLocationId && !locationExists) {
        console.log('Location mismatch detected:', {
          appointmentLocationId,
          availableLocations: locationsData.map(loc => ({ id: loc.id, name: loc.name || loc.location_name })),
          appointment
        });
      }
    }
  }, [appointment, locationsData]);

  // Fetch service categories and services
  const { data: categoriesResponse } = useServiceCategories();
  const { data: servicesResponse } = useServicesByCategory(selectedCategory);
  
  // Extract arrays from responses
  const categories = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse?.categories || []);
  const services = servicesResponse?.services || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => appointmentsApi.createAppointmentWithSync(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries([getTenantCacheKey('appointments')]);
      onClose();
    },
    onError: (error) => {
      console.error('Error creating appointment:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create appointment';
      alert(`Error: ${errorMessage}`);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsApi.updateAppointmentWithSync(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries([getTenantCacheKey('appointments')]);
      queryClient.invalidateQueries([getTenantCacheKey('appointment'), appointment?.id]);
      onClose();
    },
    onError: (error) => {
      console.error('Error updating appointment:', error);
    }
  });

  // Cancel/Delete mutation
  const cancelMutation = useMutation({
    mutationFn: (id) => appointmentsApi.cancelAppointmentWithSync(id, 'Cancelled by user'),
    onSuccess: () => {
      queryClient.invalidateQueries([getTenantCacheKey('appointments')]);
      setShowDeleteConfirm(false);
      onClose();
    },
    onError: (error) => {
      console.error('Error cancelling appointment:', error);
    }
  });

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.client_id) {
      errors.client_id = 'Client is required';
    }
    
    if (!formData.user_id) {
      errors.user_id = 'Provider is required';
    }
    
    if (!formData.location_id) {
      errors.location_id = 'Location is required';
    }
    
    if (formData.end_date <= formData.start_date) {
      errors.end_date = 'End time must be after start time';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const dataToSave = {
      ...formData,
      services: formData.services.map(s => ({
        service_id: s.service_id || s.id,
        price: s.price
      }))
    };

    if (appointment?.id) {
      updateMutation.mutate({ id: appointment.id, data: dataToSave });
    } else {
      createMutation.mutate(dataToSave);
    }
  };

  // Handle cancel/delete
  const handleDelete = () => {
    if (appointment?.id) {
      cancelMutation.mutate(appointment.id);
    }
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    let nextValue = value;

    if (field === 'status') {
      nextValue = normalizeStatus(value);
    } else if (['client_id', 'user_id', 'location_id'].includes(field)) {
      nextValue = value ?? '';
    } else if (field === 'notes') {
      nextValue = value ?? '';
    }

    setFormData(prev => ({
      ...prev,
      [field]: nextValue
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle service selection
  const handleServiceToggle = (service) => {
    setFormData(prev => {
      const existingIndex = prev.services.findIndex(s => 
        (s.service_id || s.id) === (service.service_id || service.id)
      );
      
      if (existingIndex >= 0) {
        // Remove service
        const newServices = [...prev.services];
        newServices.splice(existingIndex, 1);
        return { ...prev, services: newServices };
      } else {
        // Add service
        return {
          ...prev,
          services: [...prev.services, {
            service_id: service.id,
            service_name: service.name,
            price: service.price
          }]
        };
      }
    });
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading || cancelMutation.isLoading;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: currentTheme?.palette?.background?.paper || '#fff',
          ...dialogPaperSx
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {appointment ? 'Edit Appointment' : 'New Appointment'}
          </Typography>
          {appointment?.id && (
            <DevOnly>
              <SyncStatusIndicator appointmentId={appointment.id} size="small" />
            </DevOnly>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Row 1: Client & Status */}
            <Grid item xs={12} md={8} sx={{ ...halfWidthItemSx, minWidth: { xs: '100%', md: 360 } }}>
              <Autocomplete
                options={allClients}
                value={allClients.find(c => c.id === formData.client_id) || null}
                onChange={(event, newValue) => handleFieldChange('client_id', newValue?.id ?? '')}
                getOptionLabel={(option) => formatClientName(option)}
                loading={clientsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client *"
                    error={!!validationErrors.client_id}
                    helperText={validationErrors.client_id}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={halfWidthItemSx}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || STATUS_FALLBACK}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  label="Status"
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Start & End Date/Time */}
            <Grid item xs={12} md={6} sx={halfWidthItemSx}>
              <DateTimePicker
                label="Start Date & Time *"
                value={formData.start_date}
                onChange={(newValue) => handleFieldChange('start_date', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={halfWidthItemSx}>
              <DateTimePicker
                label="End Date & Time *"
                value={formData.end_date}
                onChange={(newValue) => handleFieldChange('end_date', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: !!validationErrors.end_date,
                    helperText: validationErrors.end_date || ''
                  }
                }}
              />
            </Grid>

            {/* Row 3: Provider & Location */}
            <Grid item xs={12} md={6} sx={halfWidthItemSx}>
              <FormControl fullWidth size="small" error={!!validationErrors.user_id}>
                <InputLabel>Provider *</InputLabel>
                <Select
                  value={formData.user_id || ''}
                  onChange={(e) => handleFieldChange('user_id', e.target.value || '')}
                  label="Provider *"
                >
                  {providersData.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      {provider.full_name || provider.name || 'Unnamed Provider'}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.user_id && (
                  <Typography variant="caption" color="error">
                    {validationErrors.user_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} sx={halfWidthItemSx}>
              <FormControl fullWidth size="small" error={!!validationErrors.location_id}>
                <InputLabel>Location *</InputLabel>
                <Select
                  value={formData.location_id || ''}
                  onChange={(e) => handleFieldChange('location_id', e.target.value || '')}
                  label="Location *"
                >
                  {locationsData.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.location_name || location.name || 'Unnamed Location'}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.location_id && (
                  <Typography variant="caption" color="error">
                    {validationErrors.location_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Row 4: Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
              />
            </Grid>

            {/* Row 5: Services */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Services
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={halfWidthItemSx}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Service Category</InputLabel>
                    <Select
                      value={selectedCategory || ''}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      label="Service Category"
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {services.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {services.map((service) => {
                    const isSelected = formData.services.some(s => 
                      (s.service_id || s.id) === service.id
                    );

                    return (
                      <Box
                        key={service.id}
                        sx={{
                          p: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: isSelected ? 'action.selected' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleServiceToggle(service)}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">
                            {service.name}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {currencySymbol}{service.price?.toFixed(2) || '0.00'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {formData.services.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Services:
                  </Typography>
                  {formData.services.map((service, index) => (
                    <Typography key={index} variant="body2">
                      • {service.service_name || service.name} - {currencySymbol}{Number(service.price || 0).toFixed(2)}
                    </Typography>
                  ))}
                  <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    Total: {currencySymbol}{formData.services.reduce((sum, s) => sum + Number(s.price || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </LocalizationProvider>

        {(createMutation.error || updateMutation.error || cancelMutation.error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createMutation.error?.message || 
             updateMutation.error?.message || 
             cancelMutation.error?.message || 
             'An error occurred'}
          </Alert>
        )}

        {showDeleteConfirm && (
          <Alert 
            severity="warning" 
            sx={{ mt: 2 }}
            action={
              <Box>
                <Button 
                  size="small" 
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  size="small" 
                  color="error" 
                  onClick={handleDelete}
                  disabled={cancelMutation.isLoading}
                >
                  Confirm
                </Button>
              </Box>
            }
          >
            Are you sure you want to cancel this appointment?
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <DevOnly>
          {appointment?.id && (
            <SyncStatusIndicator 
              appointmentId={appointment.id} 
              showDetails={true}
              size="medium"
            />
          )}
        </DevOnly>
        
        <Box sx={{ flex: 1 }} />
        
        {appointment?.id && (
          <DevOnly>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              color="error"
              disabled={isLoading || showDeleteConfirm}
              startIcon={<DeleteIcon />}
            >
              Cancel Appointment
            </Button>
          </DevOnly>
        )}
        
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          startIcon={<CancelIcon />}
        >
          Close
        </Button>
        
        <DevOnly>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {appointment ? 'Update' : 'Create'}
          </Button>
        </DevOnly>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentWriteDialog;
